import React, { useEffect, useRef } from 'react'
import useComponentSize from '@rehooks/component-size'
import { useFixedContainerSize } from '../../../views/Screens/hook'
import {
	useAnalyticsPerformance,
	useCurrentAnalytics,
	useGetAction,
	useMissionState,
	useStaticQuestions,
} from '../../../../context/provider'
import { theme } from '../../../../theme'
import { styled, ThemeProvider, css } from 'styled-components'
import { cssPlayerHeightUnit } from '../../../../utility/constants'
import Graph from './Graph'
import QuestionReview from './QuestionReview'
import { CulminatingMomentReview } from './CulminatingMomentReview'
import BasicFrame from '../../BasicFrame'
import PreviousMissions from './PreviousMissions'
import type { ConsolidatedDataViewProps } from '@mission.io/mission-player'
import type { CulminatingMomentScreen } from '@mission.io/mission-toolkit'
import SizingWrapper from '../../../basics/SizingWrapper'
import { DocumentReview } from './DocumentReview'
import { CssVariableThemeDefinitions } from '../../../CssVariablesThemeDefinitions'

/**
 * ConsolidatedDataView - a react component used to view the Performance of the class in this mission compared to previous missions
 *
 * @param  {Object} props - the react props
 * @param  {() => void} props.onFinish - a callback for when this information has finished displaying
 *
 * @returns React$Node
 */
export default function ConsolidatedDataViewUsingContext({
	onFinish,
}: {
	onFinish: () => void,
}): React$Node {
	const { pastAnalyticsPerformanceScores, currentMissionPerformanceScores } =
		useAnalyticsPerformance()
	const analytics = useCurrentAnalytics()
	const getAction = useGetAction()
	const missionState = useMissionState()

	const culminatingMoments = missionState
		? Object.keys(missionState?.culminatingMomentData ?? []).reduce(
				(data, culminatingMomentId) => {
					const action: ?CulminatingMomentScreen = (getAction(
						culminatingMomentId
					): any)
					if (!action) {
						return data
					}
					return data.concat({
						id: culminatingMomentId,
						responses:
							missionState.culminatingMomentData[culminatingMomentId]
								.responses ?? {},
						questionInfo: action.questionInfo,
					})
				},
				[]
		  )
		: []

	const staticQuestions = useStaticQuestions()

	useEffect(
		() => onFinish(),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	return (
		<Scrolling>
			<ConsolidatedDataView
				pastScores={pastAnalyticsPerformanceScores}
				currentScores={currentMissionPerformanceScores}
				analytics={analytics}
				culminatingMoments={culminatingMoments}
				questions={staticQuestions}
			/>
		</Scrolling>
	)
}

/* Displays analytics for class at the end of a mission.
 * This includes viewing class responses for questions and culminating moments.
 * This component and its children do not rely on any context other than the props provided.
 *
 *  Any updates made to this component CANNOT rely on context. This component MUST be exportable.
 */
function ConsolidatedDataView({
	analytics,
	currentScores,
	pastScores,
	culminatingMoments,
	questions,
}: ConsolidatedDataViewProps): React$Node {
	return (
		<>
			<Graph
				currentMissionScores={currentScores}
				pastAnalyticsPerformanceScores={pastScores}
			/>
			<PreviousMissions pastAnalyticsPerformanceScores={pastScores} />
			<QuestionReview analytics={analytics} questions={questions} />
			<CulminatingMomentReview
				analytics={analytics}
				culminatingMoments={culminatingMoments}
			/>
			<DocumentReview analytics={analytics} />
		</>
	)
}

/**
 * Displays analytics for class at the end of a mission and includes a lovely frame as well.
 * This includes viewing class responses for questions and culminating moments.
 * This component and its children do not rely on any context other than the props provided.
 *
 * Any updates made to this component CANNOT rely on context. This component MUST be exportable.
 * @param {ConsolidatedDataViewProps} props
 * @returns {React$Node}
 */
export function ConsolidatedDataView_StandAlone(
	props: ConsolidatedDataViewProps
): React$Node {
	return (
		<Wrapper>
			<BasicFrame>
				<Scrolling>
					<ConsolidatedDataView {...props} />
				</Scrolling>
			</BasicFrame>
		</Wrapper>
	)
}

const scrollCss = css`
	overflow-y: scroll;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	padding: ${cssPlayerHeightUnit(4)};
	padding-bottom: ${cssPlayerHeightUnit(25)};
`

const Scrolling = styled.div`
	${scrollCss}
`

const ScrollingFrame = styled(BasicFrame)`
	${scrollCss}
`

// A wrapper that makes sure the exported component uses the mission player styles and global variables, namely --ph and --pw properly.
function Wrapper({ children }: { children: React$Node }) {
	const ref = useRef()
	const wrapperSize = useComponentSize(ref)
	const { height, width } = useFixedContainerSize(
		wrapperSize.width,
		wrapperSize.height
	)

	return (
		<ThemeProvider theme={theme}>
			<CssVariableThemeDefinitions>
				<Sized ref={ref}>
					<SizingWrapper
						width={width}
						height={height}
						style={{ height: '100%' }}>
						<ScrollingFrame>{children}</ScrollingFrame>
					</SizingWrapper>
				</Sized>
			</CssVariableThemeDefinitions>
		</ThemeProvider>
	)
}

const Sized = styled.div`
	width: 100%;
	height: 100%;
`
